import { useState, useEffect } from "react";
import { MessageSquare, Send, User, Loader2, Heart, Reply, ChevronDown, ChevronUp, Trash2, Smile, AtSign, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useMember } from "../context/MemberContext";
import { Link } from "react-router-dom";

// Helper function for time ago
const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Şimdi';
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} gün önce`;
  return `${Math.floor(days / 7)} hafta önce`;
};

// Generate avatar color from name
const getAvatarColor = (name) => {
  const colors = [
    'from-amber-500 to-orange-600',
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-rose-500 to-pink-600',
    'from-violet-500 to-purple-600',
    'from-cyan-500 to-blue-600',
  ];
  const index = name?.charCodeAt(0) % colors.length || 0;
  return colors[index];
};

const Comments = ({ newsId }) => {
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyForm, setReplyForm] = useState({ content: "" });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);
  const { t } = useLang();
  const { member, token } = useMember();

  useEffect(() => { 
    fetchComments();
    if (member) {
      setForm(f => ({ ...f, name: member.name || "", email: member.email || "" }));
    }
  }, [newsId, member]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/comments/${newsId}`);
      setComments(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e, parentId = null) => {
    e.preventDefault();
    setSubmitting(true);
    
    const payload = parentId 
      ? { news_id: newsId, name: member?.name || form.name, email: member?.email || form.email, content: replyForm.content, parent_id: parentId }
      : { news_id: newsId, ...form };
    
    try {
      await axios.post(`${API}/comments`, payload);
      setMessage({ type: "success", text: parentId ? "Yanıtınız eklendi!" : "Yorumunuz paylaşıldı!" });
      
      if (parentId) {
        setReplyForm({ content: "" });
        setReplyingTo(null);
        setExpandedReplies(prev => ({ ...prev, [parentId]: true }));
      } else {
        setForm({ name: member?.name || "", email: member?.email || "", content: "" });
      }
      
      fetchComments();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Bir hata oluştu" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    if (!member || !token) {
      setMessage({ type: "error", text: "Beğenmek için giriş yapmalısınız" });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/comments/${commentId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComments(prev => prev.map(c => {
        if (c.id === commentId) {
          return { ...c, likes: res.data.likes, liked_by: res.data.liked ? [...(c.liked_by || []), member.user_id] : (c.liked_by || []).filter(id => id !== member.user_id) };
        }
        if (c.replies) {
          return { ...c, replies: c.replies.map(r => r.id === commentId ? { ...r, likes: res.data.likes, liked_by: res.data.liked ? [...(r.liked_by || []), member.user_id] : (r.liked_by || []).filter(id => id !== member.user_id) } : r) };
        }
        return c;
      }));
    } catch (err) {
      console.error("Like error:", err);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Bu yorumu silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/comments/${commentId}/member`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: "success", text: "Yorum silindi" });
      fetchComments();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Silme hatası" });
    }
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies(prev => ({ ...prev, [commentId]: !prev[commentId] }));
  };

  const isLikedByMe = (comment) => {
    return member && (comment.liked_by || []).includes(member.user_id);
  };

  const canDelete = (comment) => {
    return member && comment.email === member.email;
  };

  // Single Comment Item
  const renderComment = (comment, isReply = false) => (
    <div 
      key={comment.id} 
      className={`group ${isReply ? 'ml-12 lg:ml-14' : ''} animate-fadeIn`} 
      data-testid={`comment-${comment.id}`}
    >
      <div className={`relative ${isReply ? '' : 'bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-zinc-800/50 rounded-2xl p-4 lg:p-5 hover:border-zinc-700/50 transition-all duration-300'}`}>
        {/* Connection line for replies */}
        {isReply && (
          <div className="absolute -left-6 top-0 bottom-0 w-px bg-gradient-to-b from-zinc-700/50 to-transparent" />
        )}
        
        <div className="flex gap-3 lg:gap-4">
          {/* Avatar */}
          <div className={`relative flex-shrink-0 ${isReply ? 'w-8 h-8' : 'w-10 h-10 lg:w-11 lg:h-11'}`}>
            <div className={`w-full h-full rounded-full bg-gradient-to-br ${getAvatarColor(comment.name)} flex items-center justify-center shadow-lg`}>
              <span className={`font-bold text-white ${isReply ? 'text-xs' : 'text-sm'}`}>
                {comment.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {/* Online indicator for recent comments */}
            {getTimeAgo(comment.created_at) === 'Şimdi' && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-zinc-900" />
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className={`font-semibold text-white ${isReply ? 'text-sm' : 'text-sm lg:text-base'}`}>
                {comment.name}
              </span>
              <span className="text-[10px] lg:text-xs text-zinc-500 bg-zinc-800/50 px-2 py-0.5 rounded-full">
                {getTimeAgo(comment.created_at)}
              </span>
            </div>
            
            <p className={`text-zinc-300 leading-relaxed ${isReply ? 'text-sm' : 'text-sm lg:text-[15px]'}`}>
              {comment.content}
            </p>
            
            {/* Actions */}
            <div className="flex items-center gap-1 mt-3">
              <button
                onClick={() => handleLike(comment.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  isLikedByMe(comment) 
                    ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30' 
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50 hover:text-rose-400'
                }`}
                data-testid={`like-${comment.id}`}
              >
                <Heart className={`w-3.5 h-3.5 transition-transform ${isLikedByMe(comment) ? 'fill-current scale-110' : 'group-hover:scale-110'}`} />
                <span>{comment.likes || 0}</span>
              </button>
              
              {!isReply && (
                <button
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    replyingTo === comment.id
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50 hover:text-blue-400'
                  }`}
                  data-testid={`reply-${comment.id}`}
                >
                  <Reply className="w-3.5 h-3.5" />
                  <span>Yanıtla</span>
                </button>
              )}
              
              {canDelete(comment) && (
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-zinc-800/50 text-zinc-400 hover:bg-red-500/20 hover:text-red-400 transition-all duration-200 opacity-0 group-hover:opacity-100"
                  data-testid={`delete-${comment.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Reply Form */}
        {replyingTo === comment.id && (
          <div className="mt-4 ml-12 lg:ml-14 animate-slideDown" data-testid={`reply-form-${comment.id}`}>
            <form onSubmit={(e) => handleSubmit(e, comment.id)} className="space-y-3">
              {!member && (
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="text" 
                    value={form.name} 
                    onChange={(e) => setForm({...form, name: e.target.value})} 
                    placeholder="Adınız" 
                    className="px-3 py-2 bg-zinc-800/70 border border-zinc-700/50 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all" 
                    required 
                  />
                  <input 
                    type="email" 
                    value={form.email} 
                    onChange={(e) => setForm({...form, email: e.target.value})} 
                    placeholder="E-posta" 
                    className="px-3 py-2 bg-zinc-800/70 border border-zinc-700/50 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all" 
                    required 
                  />
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={replyForm.content}
                    onChange={(e) => setReplyForm({ content: e.target.value })}
                    placeholder={`@${comment.name}'a yanıt yaz...`}
                    className="w-full px-4 py-2.5 bg-zinc-800/70 border border-zinc-700/50 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all pr-10"
                    required
                  />
                  <Smile className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 cursor-pointer hover:text-amber-400 transition-colors" />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !replyForm.content.trim()}
                  className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-lg shadow-blue-500/20"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      
      {/* Replies Section */}
      {!isReply && comment.replies && comment.replies.length > 0 && (
        <div className="mt-3 ml-5">
          <button
            onClick={() => toggleReplies(comment.id)}
            className="flex items-center gap-2 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors mb-2"
          >
            <div className="w-6 h-px bg-zinc-700" />
            {expandedReplies[comment.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span>{comment.replies.length} yanıtı {expandedReplies[comment.id] ? 'gizle' : 'göster'}</span>
          </button>
          
          {expandedReplies[comment.id] && (
            <div className="space-y-3 animate-slideDown">
              {comment.replies.map(reply => renderComment(reply, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-10 lg:mt-14 pt-8 lg:pt-10 border-t border-zinc-800/50 bg-[#0a0a0b]" data-testid="comments-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 lg:mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-xl">
            <MessageSquare className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg lg:text-xl font-bold text-white">Yorumlar</h3>
            <p className="text-xs text-zinc-500">{comments.length} yorum</p>
          </div>
        </div>
        
        {!member && (
          <Link 
            to="/uye" 
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-xl text-amber-400 text-sm font-medium transition-all"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Giriş Yap</span>
          </Link>
        )}
      </div>

      {/* Comment Form */}
      <div className="mb-8 lg:mb-10">
        <form 
          onSubmit={(e) => handleSubmit(e)} 
          className="relative bg-gradient-to-br from-zinc-900/90 to-zinc-900/50 border border-zinc-800/50 rounded-2xl p-5 lg:p-6 space-y-4 shadow-xl"
          data-testid="comment-form"
        >
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-orange-500/5 rounded-2xl pointer-events-none" />
          
          {member ? (
            <div className="relative flex items-center gap-3 pb-4 border-b border-zinc-800/50">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(member.name)} flex items-center justify-center`}>
                <span className="font-bold text-white text-sm">{member.name?.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{member.name}</p>
                <p className="text-xs text-zinc-500">Yorum yaz...</p>
              </div>
            </div>
          ) : (
            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4 border-b border-zinc-800/50">
              <div className="relative">
                <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${focusedInput === 'name' ? 'text-amber-400' : 'text-zinc-500'}`} />
                <input 
                  type="text" 
                  value={form.name} 
                  onChange={(e) => setForm({...form, name: e.target.value})} 
                  onFocus={() => setFocusedInput('name')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder="Adınız" 
                  className="w-full pl-10 pr-4 py-3 bg-zinc-800/50 border border-zinc-700/30 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/10 transition-all" 
                  required 
                  data-testid="comment-name" 
                />
              </div>
              <div className="relative">
                <AtSign className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${focusedInput === 'email' ? 'text-amber-400' : 'text-zinc-500'}`} />
                <input 
                  type="email" 
                  value={form.email} 
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  onFocus={() => setFocusedInput('email')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder="E-posta adresiniz" 
                  className="w-full pl-10 pr-4 py-3 bg-zinc-800/50 border border-zinc-700/30 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/10 transition-all" 
                  required 
                  data-testid="comment-email" 
                />
              </div>
            </div>
          )}
          
          <div className="relative">
            <textarea 
              value={form.content} 
              onChange={(e) => setForm({...form, content: e.target.value})}
              onFocus={() => setFocusedInput('content')}
              onBlur={() => setFocusedInput(null)}
              placeholder="Düşüncelerinizi paylaşın..." 
              rows={3} 
              className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/30 rounded-xl text-white text-sm lg:text-[15px] placeholder:text-zinc-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/10 resize-none transition-all leading-relaxed" 
              required 
              data-testid="comment-content" 
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              <Smile className="w-5 h-5 text-zinc-500 cursor-pointer hover:text-amber-400 transition-colors" />
            </div>
          </div>
          
          <div className="flex items-center justify-between flex-wrap gap-3">
            {message && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <span>{message.text}</span>
              </div>
            )}
            <button 
              type="submit" 
              disabled={submitting || !form.content.trim()} 
              className="ml-auto px-5 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold text-sm rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98]" 
              data-testid="comment-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span>Paylaş</span>
            </button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="text-center py-12 lg:py-16">
          <div className="w-16 h-16 mx-auto mb-4 bg-zinc-800/50 rounded-2xl flex items-center justify-center">
            <MessageSquare className="w-8 h-8 text-zinc-600" />
          </div>
          <p className="text-zinc-400 font-medium mb-1">Henüz yorum yok</p>
          <p className="text-zinc-500 text-sm">İlk yorumu siz yapın!</p>
        </div>
      ) : (
        <div className="space-y-4 lg:space-y-5">
          {comments.map((c) => renderComment(c))}
        </div>
      )}

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Comments;
